import React, { useState, useMemo } from "react";
import {
  Box,
  Paper,
  Typography,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextField,
  Grid,
  Chip,
  CircularProgress,
  Alert,
} from "@mui/material";
import {
  DataGrid,
  GridToolbarContainer,
  GridToolbarExport,
} from "@mui/x-data-grid";
import { useZoneCalendar } from "./useZoneCalendar";
import { useDroughtStageUpdate } from "./useDroughtStageUpdate";

const columns = [
  {
    field: "zone_desc",
    headerName: "Zone",
    width: 200,
  },
  {
    field: "month_date",
    headerName: "Effective Month",
    width: 150,
  },
  {
    field: "stage_label",
    headerName: "Drought Stage",
    width: 150,
  },
  {
    field: "stage_code",
    headerName: "Stage Code",
    width: 100,
  },
  {
    field: "curtailment_pct",
    headerName: "Curtailment %",
    width: 120,
    type: "number",
    renderCell: ({ row }) => {
      const curtailment = +row.curtailment_pct || 0;
      let severity = "info";
      if (curtailment === 0) {
        severity = "success"; // blue
      } else if (curtailment === 10 || curtailment === 20) {
        severity = "warning"; // green
      } else if (curtailment >= 30) {
        severity = "error"; // red
      }

      return (
        <Chip
          label={`${curtailment}%`}
          color={severity}
          size="small"
          variant="outlined"
          width="100px"
          sx={{ width: "60px" }}
        />
      );
    },
  },
];

export default function ZoneCalendarView() {
  const [selectedZone, setSelectedZone] = useState("");
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");

  const { zonesQuery } = useDroughtStageUpdate();

  const {
    data: allCalendarData,
    isLoading: calendarLoading,
    isError: calendarError,
  } = useZoneCalendar();

  const filteredCalendarData = useMemo(() => {
    if (!allCalendarData) return [];

    let filtered = allCalendarData;

    // Filter by zone
    if (selectedZone) {
      filtered = filtered.filter(
        (item) => item.zone_ndx === parseInt(selectedZone, 10)
      );
    }

    // Filter by date range
    if (startDate || endDate) {
      filtered = filtered.filter((item) => {
        const itemDate = new Date(item.month_date);
        const startMonth = startDate ? new Date(startDate + "-01") : null;
        const endMonth = endDate ? new Date(endDate + "-01") : null;

        if (startMonth && itemDate < startMonth) return false;
        return !(endMonth && itemDate > endMonth);
      });
    }

    return filtered;
  }, [allCalendarData, selectedZone, startDate, endDate]);

  const handleZoneChange = (event) => {
    setSelectedZone(event.target.value);
  };

  const handleStartDateChange = (event) => {
    setStartDate(event.target.value);
  };

  const handleEndDateChange = (event) => {
    setEndDate(event.target.value);
  };

  // Custom toolbar with filters and export
  const CustomToolbar = () => (
    <GridToolbarContainer
      sx={{ p: 2, display: "flex", alignItems: "center", gap: 2 }}
    >
      <Grid container spacing={2} sx={{ flex: 1 }}>
        <Grid item xs={12} sm={4}>
          <FormControl fullWidth size="small">
            <InputLabel>Filter by Zone</InputLabel>
            <Select
              value={selectedZone}
              onChange={handleZoneChange}
              label="Filter by Zone"
              variant="outlined"
            >
              <MenuItem value="">
                <em>All Zones</em>
              </MenuItem>
              {zonesQuery.data?.map((zone) => (
                <MenuItem key={zone.zone_ndx} value={zone.zone_ndx}>
                  {zone.zone_desc}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Grid>

        <Grid item xs={12} sm={4}>
          <TextField
            fullWidth
            size="small"
            type="month"
            label="Start Month"
            value={startDate}
            onChange={handleStartDateChange}
            InputLabelProps={{ shrink: true }}
          />
        </Grid>

        <Grid item xs={12} sm={4}>
          <TextField
            fullWidth
            size="small"
            type="month"
            label="End Month"
            value={endDate}
            onChange={handleEndDateChange}
            InputLabelProps={{ shrink: true }}
          />
        </Grid>
      </Grid>

      <GridToolbarExport
        slotProps={{
          button: {
            variant: "outlined",
            size: "small",
          },
        }}
      />
    </GridToolbarContainer>
  );

  if (zonesQuery.isLoading) {
    return (
      <Box display="flex" justifyContent="center" p={2}>
        <CircularProgress />
      </Box>
    );
  }

  if (zonesQuery.isError) {
    return <Alert severity="error">Failed to load drought zones data.</Alert>;
  }

  return (
    <Paper sx={{ p: 3 }}>
      <Typography variant="h6" gutterBottom sx={{ mb: 2 }}>
        Monthly Drought Stage Calendar
      </Typography>

      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        View historical drought stages by month for trend analysis and
        reporting.
      </Typography>

      {calendarError && (
        <Alert severity="error" sx={{ mb: 2 }}>
          Failed to load calendar data.
        </Alert>
      )}

      <Box sx={{ height: 400, width: "100%" }}>
        <DataGrid
          rows={filteredCalendarData}
          columns={columns}
          getRowId={(row) => `calendar-${row.zone_ndx}-${row.month_date}`}
          loading={calendarLoading}
          pageSize={12}
          rowsPerPageOptions={[12, 24, 36]}
          disableSelectionOnClick
          slots={{ toolbar: CustomToolbar }}
          sx={{
            "& .MuiDataGrid-cell": {
              fontSize: "0.875rem",
            },
          }}
        />
      </Box>
    </Paper>
  );
}
